import { Modal, notification } from "antd";
import cuid from "cuid";
import dayjs from "dayjs";
import { get } from "lodash";
import { useRef, useState } from "react";
import Button from "shared/components/Button";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { useMutation } from "shared/hooks/useApi";
import { CREATE_TASK } from "utils/api/graphql/mutations/task";
import { DATE_TIME_FORMAT, TASK_STATUSES, TASK_TYPES } from "utils/constants";

function normalizeSecondsToZero(date) {
  if (date == null) return date;
  const d = dayjs(date);
  return d.isValid() ? d.second(0).millisecond(0) : date;
}

const AvatarField = ({ initials, name, label }) => (
  <div className="create-task-modal__field">
    <label>{label}</label>
    <div className="create-task-modal__avatar-field">
      <div className="avatar-badge">{initials}</div>
      <span className="avatar-name">{name}</span>
    </div>
  </div>
);

const CreateTaskModal = ({ open, onClose, contact, project, user, t }) => {
  const CREATE_TASK_T = t("DRAWER.CREATE_TASK", { returnObjects: true });

  const [title, setTitle] = useState("");
  const [instruction, setInstruction] = useState("");
  const [dueDate, setDueDate] = useState(dayjs());
  const [errors, setErrors] = useState({});

  const titleRef = useRef(null);

  const [addTask, { loading }] = useMutation(CREATE_TASK, {
    refetchQueries: ["Tasks"],
  });

  const contactFirstname = get(contact, "user.firstname", "");
  const contactLastname = get(contact, "user.lastname", "");
  const contactName = `${contactFirstname} ${contactLastname}`
    .trim()
    .toUpperCase();
  const contactInitials =
    `${contactFirstname.substring(0, 1)}${contactLastname.substring(0, 1)}`.toUpperCase();

  const ownerFirstname = get(user, "firstname", "");
  const ownerLastname = get(user, "lastname", "");
  const ownerName = `${ownerFirstname} ${ownerLastname}`.trim();
  const ownerInitials =
    `${ownerFirstname.substring(0, 1)}${ownerLastname.substring(0, 1)}`.toUpperCase();

  const validate = () => {
    const nextErrors = {};

    if (!title.trim()) {
      nextErrors.title = true;
    }
    if (!dueDate) {
      nextErrors.dueDate = true;
    }

    setErrors(nextErrors);

    if (nextErrors.title) {
      titleRef.current?.focus();
      return false;
    }

    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    addTask({
      variables: {
        data: {
          id: cuid(),
          name: title.trim(),
          type: TASK_TYPES.MANUAL,
          status: TASK_STATUSES.IN_PROGRESS,
          data: {
            instruction: instruction.trim(),
            priority: "NONE",
          },
          project: { id: get(project, "id") },
          event: {
            id: cuid(),
            startDate: normalizeSecondsToZero(dueDate).toISOString(),
            endDate: normalizeSecondsToZero(dueDate).toISOString(),
          },
        },
      },
      onCompleted: () => {
        notification.success({
          message: `${CREATE_TASK_T.SUCCESS_TITLE} "${title.trim()}" ${CREATE_TASK_T.SUCCESS_FOR} ${contactFirstname} ${contactLastname}`,
          closeIcon: null,
        });
        window.dispatchEvent(new CustomEvent("taskCreated"));
        handleClose();
      },
    });
  };

  const handleClose = () => {
    setTitle("");
    setInstruction("");
    setDueDate(dayjs());
    setErrors({});
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={CREATE_TASK_T.TITLE}
      footer={null}
      destroyOnClose
      width={680}
      wrapClassName="create-task-modal"
    >
      <div className="create-task-modal__form">
        <div className="create-task-modal__field create-task-modal__field--type">
          <Select
            label={CREATE_TASK_T.TASK_TYPE}
            value={TASK_TYPES.MANUAL}
            disabled
            options={[
              {
                label: CREATE_TASK_T.TYPES.MANUAL,
                value: TASK_TYPES.MANUAL,
              },
            ]}
          />
        </div>

        <div className="create-task-modal__field create-task-modal__field--title">
          <Input
            ref={titleRef}
            label={CREATE_TASK_T.TASK_TITLE}
            required
            placeholder={CREATE_TASK_T.TASK_TITLE_PLACEHOLDER}
            value={title}
            onChange={({ target: { value } }) => {
              setTitle(value);
              if (value.trim()) {
                setErrors((prev) => ({ ...prev, title: false }));
              }
            }}
            errors={errors.title ? " " : undefined}
            touched={errors.title}
          />
        </div>

        <div className="create-task-modal__field--contact">
          <AvatarField
            initials={contactInitials}
            name={contactName}
            label={CREATE_TASK_T.ASSOCIATED_FIELD}
            required
          />
        </div>

        <div className="create-task-modal__field create-task-modal__field--instruction">
          <Input
            type="textarea"
            label={CREATE_TASK_T.INSTRUCTION}
            placeholder={CREATE_TASK_T.INSTRUCTION_PLACEHOLDER}
            value={instruction}
            onChange={({ target: { value } }) => setInstruction(value)}
            rows={6}
          />
        </div>

        <div className="create-task-modal__field--owner">
          <AvatarField
            initials={ownerInitials}
            name={ownerName}
            label={CREATE_TASK_T.OWNER}
          />
        </div>

        <div className="create-task-modal__field create-task-modal__field--priority">
          <Select
            label={CREATE_TASK_T.PRIORITY}
            value="NONE"
            disabled
            options={[
              {
                label: CREATE_TASK_T.PRIORITIES.NONE,
                value: "NONE",
              },
            ]}
          />
        </div>

        <div className="create-task-modal__field create-task-modal__field--date">
          <Input
            type="date"
            label={CREATE_TASK_T.DUE_DATE}
            required
            showTime
            format={DATE_TIME_FORMAT}
            value={dueDate}
            onChange={(value) => {
              const normalized = normalizeSecondsToZero(value);
              setDueDate(normalized);
              if (normalized) {
                setErrors((prev) => ({ ...prev, dueDate: false }));
              }
            }}
            onBlur={() => {
              const normalized = normalizeSecondsToZero(dueDate);
              if (normalized !== dueDate) setDueDate(normalized);
            }}
            errors={errors.dueDate ? " " : undefined}
            touched={errors.dueDate}
          />
        </div>
      </div>

      <div className="create-task-modal__footer">
        <button
          className="create-task-modal__cancel"
          onClick={handleClose}
          type="button"
        >
          {CREATE_TASK_T.CANCEL}
        </button>
        <Button type="primary" onClick={handleSubmit} loading={loading}>
          {CREATE_TASK_T.SUBMIT}
        </Button>
      </div>
    </Modal>
  );
};

export default CreateTaskModal;
